import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * PhotoMiniGallery - Displays a mini-gallery of nearby photos
 *
 * Shows photos within a specified radius of a clicked point,
 * ordered by distance (closest first), then by date (most recent first).
 * Uses the shared UploadLightbox for viewing photos with full metadata.
 */
import { useState, useEffect, useCallback } from 'react';
import { UploadLightbox } from '../../../components/common/uploads/UploadLightbox';
import { processImageUrl, getFullSizeUrl } from '../../../utils/uploads';
/**
 * Convert a photo feature to the LightboxUpload format
 */
function photoToLightboxUpload(photo) {
    const props = photo.properties;
    // Use entity from API response
    let entity = null;
    if (props.entity) {
        entity = {
            type: props.entity.type,
            label: props.entity.label,
            link: props.entity.link || null,
        };
    }
    return {
        id: props.id,
        filename: props.filename,
        url: getFullSizeUrl(props.URL),
        size: props.size,
        filedate: props.filedate || null,
        uploadTime: props['Upload Time'] || null,
        mimetype: props.mimetype || null,
        entity,
    };
}
const PhotoMiniGallery = ({ coordinates, radius = 5, }) => {
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [lightboxIndex, setLightboxIndex] = useState(0);
    const fetchNearbyPhotos = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await fetch('/geo/nearby-photos', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    latlng: { lat: coordinates[1], lng: coordinates[0] },
                    radius,
                }),
            });
            if (!res.ok) {
                throw new Error(`Failed to fetch photos: ${res.status}`);
            }
            const data = await res.json();
            setPhotos(data.features || []);
        }
        catch (err) {
            console.error('[PhotoMiniGallery] Error fetching photos:', err);
            setError(err instanceof Error ? err.message : 'Failed to load photos');
        }
        finally {
            setLoading(false);
        }
    }, [coordinates, radius]);
    useEffect(() => {
        fetchNearbyPhotos();
    }, [fetchNearbyPhotos]);
    const handlePhotoClick = (index) => {
        setLightboxIndex(index);
        setLightboxOpen(true);
    };
    const handleEntityClick = (e, link) => {
        e.stopPropagation(); // Don't trigger photo click
        window.location.href = link;
    };
    const formatDistance = (distance) => {
        if (distance < 1) {
            return '<1m';
        }
        return `${Math.round(distance)}m`;
    };
    // Convert photos to lightbox format
    const lightboxUploads = photos.map(photoToLightboxUpload);
    if (loading) {
        return (_jsxs("div", { className: "photo-mini-gallery photo-mini-gallery--loading", children: [_jsx("div", { className: "photo-mini-gallery__spinner" }), _jsx("span", { children: "Loading photos..." })] }));
    }
    if (error) {
        return (_jsxs("div", { className: "photo-mini-gallery photo-mini-gallery--error", children: [_jsx("i", { className: "fas fa-exclamation-triangle" }), _jsx("span", { children: error })] }));
    }
    if (!photos.length) {
        return (_jsxs("div", { className: "photo-mini-gallery photo-mini-gallery--empty", children: [_jsx("i", { className: "fas fa-camera-slash" }), _jsxs("span", { children: ["No photos within ", radius, "m"] })] }));
    }
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "photo-mini-gallery", children: [_jsxs("div", { className: "photo-mini-gallery__header", children: [_jsxs("span", { className: "photo-mini-gallery__count", children: [photos.length, " photo", photos.length !== 1 ? 's' : ''] }), _jsxs("span", { className: "photo-mini-gallery__radius", children: ["within ", radius, "m"] })] }), _jsx("div", { className: "photo-mini-gallery__grid", children: photos.map((photo, index) => (_jsxs("div", { className: "photo-mini-gallery__item", onClick: () => handlePhotoClick(index), title: photo.properties.filename, children: [_jsx("img", { src: processImageUrl(photo.properties.URL, 'medium'), alt: photo.properties.filename, loading: "lazy", onError: (e) => {
                                        // Fallback to original URL if resized version fails
                                        const target = e.target;
                                        if (target.dataset.fallback)
                                            return;
                                        target.dataset.fallback = 'true';
                                        target.src = getFullSizeUrl(photo.properties.URL);
                                    } }), _jsx("div", { className: "photo-mini-gallery__overlay", children: _jsx("span", { className: "photo-mini-gallery__distance", children: formatDistance(photo.properties.distance) }) }), photo.properties.entity && (_jsx("div", { className: "photo-mini-gallery__entity", children: photo.properties.entity.link ? (_jsxs("a", { href: photo.properties.entity.link, onClick: (e) => handleEntityClick(e, photo.properties.entity.link), className: "photo-mini-gallery__entity-link", title: `Open ${photo.properties.entity.label}`, children: [_jsx("i", { className: `fas ${photo.properties.entity.type === 'UPRN' ? 'fa-home' : 'fa-map-marker-alt'}` }), _jsx("span", { children: photo.properties.entity.label })] })) : (_jsxs("span", { className: "photo-mini-gallery__entity-text", children: [_jsx("i", { className: `fas ${photo.properties.entity.type === 'UPRN' ? 'fa-home' : 'fa-map-marker-alt'}` }), _jsx("span", { children: photo.properties.entity.label })] })) }))] }, photo.id))) })] }), lightboxOpen && (_jsx(UploadLightbox, { uploads: lightboxUploads, currentIndex: lightboxIndex, onClose: () => setLightboxOpen(false), onIndexChange: setLightboxIndex, showTags: false, showUploader: false, showMetadata: true, showEntity: true }))] }));
};
export default PhotoMiniGallery;
